import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText, Clock, CheckCircle2, XCircle, DollarSign, ArrowRight, Loader2,
  AlertTriangle, ExternalLink, Calendar, User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILLS = {
  pending:            { label: 'Awaiting response', cls: 'bg-zinc-500/15 text-zinc-200 border-zinc-400/30' },
  declined:           { label: 'Declined by vendor', cls: 'bg-red-500/15 text-red-200 border-red-400/30' },
  rejected_by_buyer:  { label: 'You rejected the offer', cls: 'bg-red-500/15 text-red-200 border-red-400/30' },
  pending_payment:    { label: 'Pay now to start', cls: 'bg-yellow-400/20 text-yellow-100 border-yellow-300/50' },
  active:             { label: 'In progress', cls: 'bg-blue-500/15 text-blue-200 border-blue-400/30' },
  delivered:          { label: 'Delivered — review it', cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
  completed:          { label: 'Completed', cls: 'bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-400/30' },
  cancelled:          { label: 'Cancelled', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-400/20' },
  revision_requested: { label: 'Revision requested', cls: 'bg-orange-500/15 text-orange-200 border-orange-400/30' },
  accepted:           { label: 'Accepted — awaiting offer', cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30' },
};

function StatusPill({ status }) {
  const def = STATUS_PILLS[status] || STATUS_PILLS.pending;
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${def.cls}`}>
      {def.label}
    </span>
  );
}

/**
 * Consumer Inquiries page — `/dashboard/consumer/inquiries`
 *
 * Lists every inquiry the consumer has sent. Highlights accepted ones with
 * a gold pulsing "Pay to start" button, and pending-payment orders with a
 * "Reject offer" secondary button.
 */
export default function ConsumerInquiries() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/messages/inquiries/mine`, { headers: getAuthHeaders() });
      if (res.ok) setItems((await res.json()).inquiries || []);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const rejectOffer = async (orderId) => {
    if (!window.confirm('Reject this offer? The vendor will be notified. You can always send a new inquiry later.')) return;
    setRejectingId(orderId);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/buyer-reject`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (r.ok) { toast.success('Offer rejected. Vendor has been notified.'); load(); }
      else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Failed to reject offer'); }
    } finally { setRejectingId(null); }
  };

  const syncPayment = async (orderId) => {
    setRejectingId(orderId); // reuse loading state slot
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/sync-payment`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.status && d.status !== 'pending_payment') {
        toast.success('Payment confirmed! Status updated.');
        load();
      } else if (r.ok) {
        toast.info("Stripe hasn't seen the payment yet — try again in a moment, or refresh.");
      } else {
        toast.error(d.detail || 'Could not check payment status');
      }
    } finally { setRejectingId(null); }
  };

  const payNow = (orderId) => navigate(`/orders/${orderId}`);

  return (
    <div className="space-y-6" data-testid="consumer-inquiries-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">My Inquiries</h1>
          <p className="text-sm text-zinc-400 mt-1">Track every custom job you've sent to a vendor.</p>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-fuchsia-300" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center" data-testid="inquiries-empty">
          <FileText size={40} className="mx-auto text-zinc-500 mb-3" />
          <p className="text-white font-bold">No inquiries yet</p>
          <p className="text-zinc-400 text-sm mt-1">Browse vendors and send your first custom project request.</p>
          <Link to="/browse" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-sm font-bold">
            Browse vendors <ArrowRight size={14} />
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-3">
          {items.map((inq) => {
            const order = inq.order;
            const isPendingPayment = order && order.status === 'pending_payment';
            return (
              <div
                key={inq.message_id}
                className="rounded-2xl border border-white/10 bg-[#13153A]/60 backdrop-blur-xl p-5 hover:border-fuchsia-400/30 transition-all"
                data-testid={`inquiry-row-${inq.message_id}`}
              >
                {/* Gold pulsing pay reminder banner — only for pending_payment */}
                {isPendingPayment && (
                  <div
                    className="mb-4 rounded-xl border-2 border-yellow-300/70 bg-gradient-to-r from-yellow-400/15 via-amber-400/10 to-yellow-300/15 p-3.5 flex flex-col sm:flex-row sm:items-center gap-3"
                    style={{ animation: 'pay-glow 1.6s ease-in-out infinite' }}
                    data-testid="pay-reminder"
                  >
                    <div className="w-10 h-10 rounded-xl bg-yellow-400 flex items-center justify-center shadow-[0_0_20px_rgba(252,211,77,0.65)] flex-shrink-0">
                      <DollarSign size={18} className="text-[#0E0E23]" strokeWidth={3} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-yellow-100 font-bold text-sm">
                        Offer accepted! Pay <span className="text-yellow-50">${order.price?.toFixed(2)}</span> to start the {order.delivery_days}-day delivery.
                      </p>
                      {order.scope_note && (
                        <p className="text-yellow-100/80 text-xs mt-1 italic">"{order.scope_note.slice(0, 140)}"</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => payNow(order.order_id)}
                        className="px-4 py-2 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-[#0E0E23] text-xs font-bold inline-flex items-center gap-1.5 transition-all"
                        data-testid={`pay-now-${inq.message_id}`}
                      >
                        Pay now <ArrowRight size={13} />
                      </button>
                      <button
                        onClick={() => rejectOffer(order.order_id)}
                        disabled={rejectingId === order.order_id}
                        className="px-3 py-2 rounded-xl border border-red-400/50 hover:bg-red-500/10 text-red-100 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid={`reject-offer-${inq.message_id}`}
                      >
                        {rejectingId === order.order_id ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject offer
                      </button>
                    </div>
                  </div>
                )}

                {isPendingPayment && (
                  <button
                    onClick={() => syncPayment(order.order_id)}
                    disabled={rejectingId === order.order_id}
                    className="-mt-2 mb-3 text-yellow-200/80 hover:text-yellow-100 text-[11px] font-semibold underline decoration-yellow-300/40 underline-offset-2 disabled:opacity-50"
                    data-testid={`sync-payment-${inq.message_id}`}
                  >
                    Already paid? Refresh payment status →
                  </button>
                )}

                {/* Inquiry summary */}
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-fuchsia-500/15 border border-fuchsia-400/20 flex items-center justify-center flex-shrink-0">
                    {inq.vendor_logo
                      ? <img src={inq.vendor_logo} alt="" className="w-full h-full object-cover rounded-xl" />
                      : <UserIcon size={18} className="text-fuchsia-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-bold text-sm truncate">{inq.title}</p>
                        <p className="text-zinc-400 text-xs mt-0.5 truncate">
                          To <span className="text-fuchsia-200">{inq.vendor_name}</span>
                          {inq.created_at && <span> · {new Date(inq.created_at).toLocaleDateString()}</span>}
                        </p>
                      </div>
                      <StatusPill status={inq.status} />
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                      {inq.budget && (
                        <span className="inline-flex items-center gap-1">
                          <DollarSign size={11} /> Budget: <span className="text-white font-semibold">{inq.budget}</span>
                        </span>
                      )}
                      {inq.deadline && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={11} /> By {inq.deadline}
                        </span>
                      )}
                      {(inq.attachment_urls || []).length > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <FileText size={11} /> {inq.attachment_urls.length} reference{inq.attachment_urls.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>

                    {/* Footer actions */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {order && (
                        <Link
                          to={`/orders/${order.order_id}`}
                          className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-fuchsia-200 hover:text-white text-xs font-semibold inline-flex items-center gap-1.5"
                          data-testid={`view-order-${inq.message_id}`}
                        >
                          View order <ExternalLink size={11} />
                        </Link>
                      )}
                      <Link
                        to={`/messages/${inq.vendor_user_id}`}
                        className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white text-xs font-semibold inline-flex items-center gap-1.5"
                      >
                        Message vendor
                      </Link>
                      {inq.status === 'declined' && (
                        <span className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-200 text-xs inline-flex items-center gap-1.5">
                          <AlertTriangle size={11} /> {inq.inquiry_meta?.decline_reason || 'No reason given'}
                        </span>
                      )}
                      {inq.status === 'pending' && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500">
                          <Clock size={10} /> Waiting on the vendor
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes pay-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(252, 211, 77, 0.35); }
          50%      { box-shadow: 0 0 26px 6px rgba(252, 211, 77, 0.55); }
        }
      `}</style>
    </div>
  );
}
